import React, { useState } from 'react'
import {
  Shirt,
  Compass,
  User,
  Dumbbell,
  Smartphone,
  Goal,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  ShoppingCart,
  Home,
  Zap,
  DollarSign,
  LucideIcon
} from 'lucide-react'
import {
  CurrencyEnum,
  Multiplier,
  SkillEnum,
  UnlockRule,
  Upgrade,
  UpgradeCategoryEnum,
  UpgradeEnum,
  GamePlayData,
  StatsEnum,
  UpgradeBoardEnum,
  UpgradeHousingEnum
} from '@/types'
import { SkillPill } from '../shared/SkillPill'
import { useGameplayContext, IGameplayContext } from '@/contexts/gameplayContext'
import { UPGRADE_CATEGORIES, UPGRADES } from '@/constants/upgrades'
import { checkUnlockRules, formatCurrency } from '@/utils'
import { SurfboardIcon } from '@/icons/SurfboardIcon'
import { Tabs } from '../shared/Tabs'
import { Select, SelectItem } from '@nextui-org/react'

type TabType = 'shop' | 'inventory'

const Card = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={`rounded-lg shadow-md ${className}`}>{children}</div>
)

const CardContent = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={`p-6 ${className}`}>{children}</div>
)

const Button = ({
  children,
  variant = 'default',
  className = '',
  ...props
}: {
  children: React.ReactNode
  variant?: string
  className?: string
  onClick?: () => void
}) => {
  const baseClass = 'px-4 py-2 rounded-md font-medium flex items-center whitespace-nowrap'
  const variantClass = variant === 'outline' ? className + ' border border-gray-300' : 'bg-blue-500 text-white '
  return (
    <button className={`${baseClass} ${variantClass}`} {...props}>
      {children}
    </button>
  )
}

const categories = [
  { key: 'game_progression', icon: Goal, color: 'bg-purple-100 border-purple-300 text-purple-800' },
  { key: 'surfboards', icon: SurfboardIcon, color: 'bg-blue-100 border-blue-300 text-blue-800' },
  { key: 'coaching', icon: User, color: 'bg-pink-100 border-pink-300 text-pink-800' },
  { key: 'digital_tools', icon: Smartphone, color: 'bg-yellow-100 border-yellow-300 text-yellow-800' },
  { key: 'fitness_gyms', icon: Dumbbell, color: 'bg-cyan-100 border-cyan-300 text-cyan-800' },
  { key: 'surf_gear_accessories', icon: Shirt, color: 'bg-slate-100 border-slate-300 text-slate-800' },
  { key: 'housing', icon: Home, color: 'bg-stone-100 border-stone-300 text-stone-800' }
]

const getEffectIcon = (effect: Multiplier): LucideIcon | undefined => {
  if (effect.effectCategory === 'currency') {
    if (effect.key === 'energy') return Zap
    if (effect.key === 'money') return DollarSign
  }
  return undefined
}

const EquippedSurfboardCard = () => {
  const { gameData, changeEquipment } = useGameplayContext() as IGameplayContext
  const ownedSurfboards = Object.values(UPGRADES).filter(
    (upgrade) => upgrade.category === 'surfboards' && gameData.upgrades[upgrade.key]?.isPurchased
  )

  const handleBoardChange = (key: UpgradeBoardEnum) => {
    changeEquipment('surfboard', key)
  }

  return (
    <Card className="bg-blue-100 border-blue-300">
      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <span className="text-2xl">🏄‍♂️</span>
            <span className="font-semibold">Surfboard</span>
          </div>
        </div>
        <Select
          label="Select Surfboard"
          selectedKeys={gameData.equipment.surfboard ? [gameData.equipment.surfboard] : []}
          onSelectionChange={(keys) => handleBoardChange(Array.from(keys)[0] as UpgradeBoardEnum)}
          className="w-full mb-4"
          classNames={{
            trigger: 'h-10',
            value: 'text-small',
            label: 'text-tiny'
          }}
          size="sm"
        >
          {ownedSurfboards.map((board) => (
            <SelectItem key={board.key} value={board.key}>
              {board.name}
            </SelectItem>
          ))}
        </Select>
        {gameData.equipment.surfboard && UPGRADES[gameData.equipment.surfboard]?.effects && (
          <div className="flex flex-wrap gap-1">
            {UPGRADES[gameData.equipment.surfboard].effects.map((effect: Multiplier, index: number) => (
              <SkillPill key={index} skill={effect.key} value={effect.value} Icon={getEffectIcon(effect)} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

const HousingCard = () => {
  const { gameData, changeEquipment } = useGameplayContext() as IGameplayContext
  const housingUpgrades = Object.values(UPGRADES).filter(
    (upgrade) => upgrade.category === 'housing' && gameData.upgrades[upgrade.key]?.isPurchased
  )

  const handleHousingChange = (key: UpgradeHousingEnum) => {
    changeEquipment('housing', key)
  }

  return (
    <Card className="bg-stone-100 border-stone-300">
      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <span className="text-2xl">🏠</span>
            <span className="font-semibold">Housing</span>
          </div>
        </div>
        <Select
          label="Select Housing"
          selectedKeys={gameData.equipment.housing ? [gameData.equipment.housing] : []}
          onSelectionChange={(keys) => handleHousingChange(Array.from(keys)[0] as UpgradeHousingEnum)}
          className="w-full mb-4"
          classNames={{
            trigger: 'h-10',
            value: 'text-small',
            label: 'text-tiny'
          }}
          size="sm"
        >
          {housingUpgrades.map((house) => (
            <SelectItem key={house.key} value={house.key}>
              {house.name}
            </SelectItem>
          ))}
        </Select>
        {gameData.equipment.housing && UPGRADES[gameData.equipment.housing]?.effects && (
          <div>
            <p className="text-sm mb-2 font-bold"> Hourly Rent: ${UPGRADES[gameData.equipment.housing].hourlyFee}</p>

            <p className="text-sm mb-2">{UPGRADES[gameData.equipment.housing].description}</p>
            <div className="flex flex-wrap gap-1">
              {UPGRADES[gameData.equipment.housing].effects.map((effect, index) => (
                <SkillPill key={index} skill={effect.key} value={effect.value} Icon={getEffectIcon(effect)} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

const OtherEquipmentCard = () => {
  const { gameData, changeEquipment } = useGameplayContext() as IGameplayContext

  const otherItems = Object.values(UPGRADES)
    .filter(
      (upgrade) =>
        (upgrade.category === 'coaching' ||
          upgrade.category === 'digital_tools' ||
          upgrade.category === 'fitness_gyms' ||
          upgrade.category === 'surf_gear_accessories') &&
        gameData.upgrades[upgrade.key]?.isPurchased
    )
    .sort((a, b) => {
      // Sort by hourlyFee in descending order, items without fees go last
      if (a.hourlyFee && b.hourlyFee) {
        return b.hourlyFee - a.hourlyFee
      }
      if (a.hourlyFee) return -1
      if (b.hourlyFee) return 1
      return 0
    })

  const handleItemToggle = (itemKey: UpgradeEnum) => {
    const newSelection = gameData.equipment.other.includes(itemKey)
      ? gameData.equipment.other.filter((key) => key !== itemKey)
      : [...gameData.equipment.other, itemKey]

    changeEquipment('other', newSelection)
  }

  const categoryEmojis: { [key: string]: string } = {
    coaching: '👨‍🏫',
    digital_tools: '📱',
    fitness_gyms: '🏋️‍♂️',
    surf_gear_accessories: '🎽'
  }

  return (
    <Card className="bg-pink-100 border-pink-300">
      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <span className="text-2xl">⚡</span>
            <span className="font-semibold">Other Equipment</span>
          </div>
        </div>
        <div className={`max-h-48 overflow-y-auto pr-1 ${otherItems.length > 5 ? 'custom-scrollbar' : ''}`}>
          {otherItems.map((item) => (
            <div key={item.key} className="flex items-center p-2 bg-white rounded-md mb-2">
              <input
                type="checkbox"
                checked={gameData.equipment.other.includes(item.key)}
                onChange={() => handleItemToggle(item.key)}
                className="mr-2 h-4 w-4"
              />
              <div className="flex-1 flex items-center">
                <span className="mr-2">{categoryEmojis[item.category]}</span>
                <span className="font-medium">{item.name}</span>
                {item.hourlyFee ? (
                  <span className="text-xs text-gray-500 font-bold ml-1">(${item.hourlyFee}/hr)</span>
                ) : null}
              </div>
              <div className="flex flex-1 justify-end items-center gap-2">
                {item.effects.map((effect: Multiplier, index: number) => (
                  <SkillPill
                    key={`${item.key}-${index}`}
                    skill={effect.key}
                    value={effect.value}
                    Icon={getEffectIcon(effect)}
                    small
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

const UpgradeCard = ({
  upgrade,
  money,
  isShop,
  defaultExpanded = false
}: {
  upgrade: Upgrade
  money: number
  isShop: boolean
  defaultExpanded: boolean
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const { gameData, purchaseUpgrade } = useGameplayContext()
  const categoryInfo = categories.find((c) => c.key === upgrade.category)
  const Icon = categoryInfo?.icon || Compass
  const canAfford = money >= upgrade.cost
  const isPurchased = gameData.upgrades[upgrade.key]?.isPurchased

  const handlePurchase = () => {
    if (!canAfford || isPurchased) return
    purchaseUpgrade(upgrade.key)
  }

  return (
    <Card className={`${categoryInfo?.color} overflow-hidden h-fit`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <div className="flex items-center space-x-2">
              <Icon className="w-6 h-6" />
              <span className="font-semibold">{upgrade.name}</span>
            </div>
            {!!upgrade.hourlyFee && (
              <span className="text-xs mt-1 text-gray-700">
                Hourly fee when equipped: <span className="font-bold">${upgrade.hourlyFee}</span>
              </span>
            )}
          </div>
          {isShop && (
            <button
              onClick={handlePurchase}
              disabled={!canAfford || isPurchased}
              className={`ml-auto mr-2 px-4 py-2 rounded-md font-medium flex items-center gap-2 transition-colors
                ${
                  isPurchased
                    ? 'bg-blue-500 text-white cursor-default'
                    : canAfford
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : 'bg-red-200 text-red-700 cursor-not-allowed'
                }`}
            >
              {isPurchased ? (
                <>
                  <span>Purchased</span>
                  <CheckCircle className="w-4 h-4" />
                </>
              ) : (
                <>
                  <span>${formatCurrency(upgrade.cost)}</span>
                  <ShoppingCart className="w-4 h-4" />
                </>
              )}
            </button>
          )}
          <button className="p-0 hover:opacity-75 transition-opacity" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </CardContent>
      <div className={`transition-all duration-200 ${isExpanded ? 'max-h-96' : 'max-h-0'} overflow-hidden`}>
        <CardContent className="pt-0">
          <p className="text-sm mb-2">{upgrade.description}</p>
          <div className="flex flex-wrap gap-1 mb-2">
            {upgrade.effects.map((effect, index) => (
              <SkillPill key={index} skill={effect.key} value={effect.value} Icon={getEffectIcon(effect)} />
            ))}
          </div>
        </CardContent>
      </div>
    </Card>
  )
}

export function UpgradesScreen() {
  const [selectedCategories, setSelectedCategories] = useState(['ALL'])
  const { gameData, setGameData } = useGameplayContext()
  const money = gameData.currencies.money.amount

  const handleTabChange = (value: string) => {
    setGameData((prev) => ({
      ...prev,
      tabs: {
        ...prev.tabs,
        upgrades: value
      }
    }))
  }

  const handleCategoryToggle = (category: UpgradeCategoryEnum | 'ALL') => {
    setSelectedCategories((prev) => {
      if (category === 'ALL') {
        return ['ALL']
      }
      const newCategories = prev.filter((c) => c !== 'ALL')
      if (newCategories.includes(category)) {
        return newCategories.filter((c) => c !== category)
      } else {
        return [...newCategories, category]
      }
    })
  }

  const filteredUpgrades = Object.values(UPGRADES).filter((upgrade) => {
    const categoryMatch = selectedCategories.includes('ALL') || selectedCategories.includes(upgrade.category)
    const isPurchased = gameData.upgrades[upgrade.key]?.isPurchased
    const isVisible = checkUnlockRules(upgrade.visibilityRules, gameData)

    if (gameData.tabs.upgrades === 'shop') {
      return categoryMatch && !isPurchased && isVisible
    } else {
      return categoryMatch && isPurchased
    }
  })

  const tabs = [
    { value: 'shop' as const, label: '🛍️ Shop' },
    { value: 'inventory' as const, label: '🗃️ Inventory' }
  ]

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold text-white drop-shadow-md">🛍️ Upgrades</h2>
      <div>
        <Tabs tabs={tabs} activeTab={gameData.tabs.upgrades} onChange={handleTabChange} />

        {gameData.tabs.upgrades === 'inventory' && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-xl font-bold text-white">Equipment</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <EquippedSurfboardCard />
              <HousingCard />
              <div className="col-span-2">
                <OtherEquipmentCard />
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-white">All Owned Items</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                <Button
                  variant={selectedCategories.includes('ALL') ? 'default' : 'outline'}
                  onClick={() => handleCategoryToggle('ALL')}
                >
                  ALL
                </Button>

                {categories.map((category) => (
                  <Button
                    key={category.key}
                    variant={selectedCategories.includes(category.key) ? 'default' : 'outline'}
                    className={`${category.color}`}
                    onClick={() => handleCategoryToggle(category.key as UpgradeCategoryEnum)}
                  >
                    <category.icon className="w-4 h-4 mr-2" />
                    {UPGRADE_CATEGORIES[category.key as UpgradeCategoryEnum].name}
                  </Button>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredUpgrades.map((upgrade) => (
                  <UpgradeCard
                    key={upgrade.key}
                    upgrade={upgrade as Upgrade}
                    money={money}
                    isShop={false}
                    defaultExpanded={false}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        {gameData.tabs.upgrades === 'shop' && (
          <div>
            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                variant={selectedCategories.includes('ALL') ? 'default' : 'outline'}
                onClick={() => handleCategoryToggle('ALL')}
              >
                ALL
              </Button>

              {categories.map((category) => (
                <Button
                  key={category.key}
                  variant={selectedCategories.includes(category.key) ? 'default' : 'outline'}
                  className={`${category.color}`}
                  onClick={() => handleCategoryToggle(category.key as UpgradeCategoryEnum)}
                >
                  <category.icon className="w-4 h-4 mr-2" />
                  {UPGRADE_CATEGORIES[category.key as UpgradeCategoryEnum].name}
                </Button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {filteredUpgrades.map((upgrade) => (
                <UpgradeCard
                  key={upgrade.key}
                  upgrade={upgrade as Upgrade}
                  money={money}
                  isShop={true}
                  defaultExpanded={false}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
